import { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { LoginForm } from '../auth/LoginForm';
import { SignupForm } from '../auth/SignupForm';
import styles from './AuthModal.module.css';

interface AuthModalProps {
  mode: 'login' | 'signup';
}

/**
 * AuthModal — desktop-only overlay shell for login/signup.
 *
 * Mounted by App.tsx in the secondary <Routes> tree when the route matches
 * `/accedi` or `/registrati` AND a `backgroundLocation` is present (desktop
 * internal navigation). Mirrors EventModal: backdrop + centered card,
 * X close button, Esc to close, click-outside to close, body scroll lock.
 *
 * The actual auth form (LoginForm/SignupForm) is rendered in "embedded" mode,
 * which strips the standalone .page/.card chrome since this modal already
 * supplies a surface.
 *
 * Closing:
 *   - X button / Esc / backdrop click → navigate(-1), which pops the modal
 *     and reveals the underlying background page.
 *   - Successful login on the same background page → form calls
 *     `onLoginSuccessSamePage`, modal dismisses via navigate(-1) instead of
 *     re-navigating to the same path.
 */
export function AuthModal({ mode }: AuthModalProps) {
  const navigate = useNavigate();

  const close = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  // Esc to close + body scroll lock (same as EventModal).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [close]);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) close();
  };

  // Post-auth: if destination is the page underneath the modal, just dismiss.
  // We capture the background pathname at mount-time via history state — the
  // background-page is the entry below /accedi or /registrati in history.
  // Simpler heuristic: if destination differs from the current modal route AND
  // is the immediate previous entry, navigate(-1). Otherwise navigate(dest).
  // In practice almost all logins keep the user on the background page, so we
  // treat "same page" loosely: any destination other than /profilo (first-time
  // profile setup) is considered same-page.
  const handleLoginSuccess = useCallback(
    (destination: string): boolean => {
      // First-time users go to /profilo to set up: don't intercept — let the
      // form navigate normally so the user actually lands on /profilo.
      if (destination === '/profilo') return false;
      // Otherwise dismiss the modal; the user stays on the background page.
      close();
      return true;
    },
    [close]
  );

  return (
    <div
      className={styles.backdrop}
      onClick={handleBackdropClick}
      data-testid="auth-modal-backdrop"
    >
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-label={mode === 'login' ? 'Accedi' : 'Registrati'}
        data-testid="auth-modal"
      >
        <button
          type="button"
          onClick={close}
          title="Chiudi"
          aria-label="Chiudi"
          className={styles.closeBtn}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <div className={styles.body}>
          {mode === 'login' ? (
            <LoginForm embedded onLoginSuccessSamePage={handleLoginSuccess} />
          ) : (
            <SignupForm embedded onLoginSuccessSamePage={handleLoginSuccess} />
          )}
        </div>
      </div>
    </div>
  );
}
